import React, { useMemo, useRef, useState } from "react"
import { DateTime } from "luxon"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveServiceCenterId } from "../../../../../context/contextSelectors"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import {
    fetchCancellationRatePerResource,
    ServiceCenterCancellationRateReportResponse,
    ServiceCenterStatisticsQuery,
} from "../../statisticsApi"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { StandardChip } from "../../../../../../shared/components/badges/StandardChip"
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table"
import { CancellationRateEntry } from "../../statisticsTypes"
import { ResourceTypeFilter } from "../common/ResourceTypeFilter"
import { ResourceType } from "../../../../admin/organization/resources/resourcesTypes"
import { useExportRateEntry } from "../../hooks/useExportRateEntry"
import { useDateTime } from "../../../../../localization/useDateTime"

const selectCancellations = (data: ServiceCenterCancellationRateReportResponse) => data?.cancellationRates || []

export function Cancellations() {
    const serviceCenterId = useAppSelector(selectEffectiveServiceCenterId)
    const [selectedResource, setSelectedResource] = useState<ResourceType>(ResourceType.ROOM)
    const { l10n } = useAppLocalization()
    const { startDate, endDate } = useInitialDateFilters()
    const dateTimeFactory = useDateTime()
    const today = dateTimeFactory.now()
    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate: startDate,
        endDate: endDate,
    })
    const tableInstanceRef = useRef<MRT_TableInstance<CancellationRateEntry>>(null)

    const cancellationsResult = useApiQuery(fetchCancellationRatePerResource, {
        queryName: ServiceCenterStatisticsQuery.SERVICE_CENTER_STATISTICS_CANCELLATION_RATE_PER_RESOURCE,
        dependencies: {
            serviceCenterId: serviceCenterId ?? 0,
            resourceType: selectedResource,
            startDate: dateState.startDate.toISODate(),
            endDate: dateState.endDate.toISODate(),
        },
        enabled: Boolean(serviceCenterId),
        select: selectCancellations,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(cancellationsResult)

    const columns = useMemo<MRT_ColumnDef<CancellationRateEntry>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("service-center-reports-statistic-occupancy-table-resource-title"),
                accessorKey: "resource.displayName",
            }),

            columnFactory.nameColumn({
                header: l10n.getString("service-center-reports-statistic-cancellations-table-cancellation-rate-title"),
                accessorKey: "cancellationRate",
            }),
            columnFactory.statusColumn({
                header: l10n.getString("service-center-reports-statistic-occupancy-table-status-title"),
                id: "visibility",
                Cell: ({ row }) => {
                    return (
                        <StandardChip
                            label={row.original.status.label}
                            variant={row.original.status.variant}
                            placement="table"
                        />
                    )
                },
            }),
        ],
        [l10n, columnFactory],
    )

    const exportCancellations = useExportRateEntry({
        data: cancellationsResult.data,
        startDate: dateState.startDate || today,
        endDate: dateState.endDate || today,
    })

    return (
        <>
            <ApiQueryTable
                tableInstanceRef={tableInstanceRef}
                columns={columns}
                initialState={{ density: "spacious" }}
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" table={table}>
                        <StandardExportButton
                            onClick={() => {
                                exportCancellations()
                            }}
                        />
                    </StandardTableFilter>
                )}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <LocalizedStrict
                            id="service-center-reports-statistic-occupancy-table-date-range-filter"
                            attrs={{ label: true }}
                        >
                            <StandardDateRangeFilter value={dateState} onChange={setDateState} label="Date range" />
                        </LocalizedStrict>
                        <ResourceTypeFilter value={selectedResource} onChange={setSelectedResource} />
                    </StandardTableFilter>
                )}
            />
        </>
    )
}
